import React, { useRef } from 'react';
import useScreenLoop from '../hooks/useScreenLoop';
import './StoryboardRun.css';

// The value is how many frames have been drawn.
const LOOP = {
  initial: 0,
  final: 3,
  steps: [
    [1, 760],
    [2, 760],
    [3, 3600],
    [0, 1300],
  ],
};

// The scene from ScreenplaySnap, boarded. Art is in a 160 x 90 viewBox:
// silhouettes and fills first, then the gold line work drawn over them.
const WIDE = (
  <>
    <circle className="sb-fill sb-glow" cx="124" cy="24" r="15" />
    <circle className="sb-fill sb-moon" cx="124" cy="24" r="7" />
    <circle className="sb-fill sb-star" cx="18" cy="14" r="1.1" />
    <circle className="sb-fill sb-star" cx="36" cy="8" r="0.9" />
    <circle className="sb-fill sb-star" cx="56" cy="21" r="1" />
    <circle className="sb-fill sb-star" cx="74" cy="10" r="0.9" />
    <circle className="sb-fill sb-star" cx="98" cy="17" r="1.1" />
    <circle className="sb-fill sb-star" cx="148" cy="44" r="0.9" />
    <path className="sb-fill sb-ground" d="M0 70 Q40 63 80 67 T160 65 V90 H0 Z" />
    <path
      className="sb-fill sb-stone"
      fillRule="evenodd"
      d="M76 67 V52 A12 12 0 0 1 100 52 V67 H96 V53 A8 8 0 0 0 80 53 V67 Z"
    />
    <g className="sb-fill sb-figure">
      <circle cx="40" cy="57.6" r="2" />
      <path d="M38.4 66 L39 60.2 H41 L41.6 66 Z" />
      <circle cx="47" cy="56.8" r="2.2" />
      <path d="M45.2 66 L45.9 59.6 H48.1 L48.8 66 Z" />
      <path d="M50 66 V54.6 H50.8 V66 Z" />
    </g>
    <path className="sb-line" pathLength="1" d="M0 70 Q40 63 80 67 T160 65" />
    <path className="sb-line" pathLength="1" d="M76 67 V52 A12 12 0 0 1 100 52 V67" />
    <path className="sb-line sb-line--fine" pathLength="1" d="M80 67 V53 A8 8 0 0 1 96 53 V67" />
  </>
);

const TWO_SHOT = (
  <>
    <circle className="sb-fill sb-glow" cx="80" cy="30" r="13" />
    <circle className="sb-fill sb-moon" cx="80" cy="30" r="6" />
    <circle className="sb-fill sb-star" cx="62" cy="20" r="0.9" />
    <circle className="sb-fill sb-star" cx="101" cy="16" r="1" />
    <path className="sb-fill sb-ground" d="M0 84 H160 V90 H0 Z" />
    <path
      className="sb-fill sb-stone"
      fillRule="evenodd"
      d="M22 90 V46 A58 58 0 0 1 138 46 V90 H120 V48 A40 40 0 0 0 40 48 V90 Z"
    />
    <g className="sb-fill sb-figure">
      <circle cx="68" cy="60" r="4.2" />
      <path d="M61 84 L63.4 67 Q68 64.4 72.6 67 L75 84 Z" />
      <circle cx="92" cy="56.5" r="4.4" />
      <path d="M84 84 L86.6 63.6 Q92 60.6 97.4 63.6 L101 84 Z" />
      <path d="M103.6 84 V53 H105 V84 Z" />
    </g>
    <path className="sb-line" pathLength="1" d="M22 90 V46 A58 58 0 0 1 138 46 V90" />
    <path className="sb-line" pathLength="1" d="M40 90 V48 A40 40 0 0 1 120 48 V90" />
    <path className="sb-line sb-line--fine" pathLength="1" d="M40 84 H120" />
  </>
);

// Virgil in profile, facing the gate; the outline stays open along the
// bottom edge so the line work does not trace the frame.
const HEAD_EDGE =
  'M108 90 L106 72 Q98 72 92 66 Q89 62 89 59 Q91 57 90 56 Q88 55 88 54 Q91 52 90 50 ' +
  'L84 46 L92 34 Q94 16 114 14 Q138 14 140 38 Q142 56 132 66 L138 90';

const CLOSE = (
  <>
    <circle className="sb-fill sb-glow" cx="40" cy="24" r="12" />
    <circle className="sb-fill sb-moon" cx="40" cy="24" r="5" />
    <circle className="sb-fill sb-star" cx="18" cy="44" r="1" />
    <circle className="sb-fill sb-star" cx="64" cy="12" r="0.9" />
    <circle className="sb-fill sb-star" cx="70" cy="40" r="0.8" />
    <path className="sb-fill sb-head" d={`${HEAD_EDGE} Z`} />
    <path className="sb-line" pathLength="1" d={HEAD_EDGE} />
    <path className="sb-line sb-line--fine" pathLength="1" d="M96 28 Q116 12 138 30" />
    <g className="sb-fill sb-laurel">
      <ellipse cx="102" cy="24" rx="3.2" ry="1.3" transform="rotate(-48 102 24)" />
      <ellipse cx="110.2" cy="21" rx="3.2" ry="1.3" transform="rotate(-24 110.2 21)" />
      <ellipse cx="118.6" cy="20.7" rx="3.2" ry="1.3" transform="rotate(12 118.6 20.7)" />
      <ellipse cx="127.1" cy="23.1" rx="3.2" ry="1.3" transform="rotate(34 127.1 23.1)" />
      <ellipse cx="134.5" cy="27.3" rx="3.2" ry="1.3" transform="rotate(52 134.5 27.3)" />
    </g>
    <path className="sb-line sb-line--fine" pathLength="1" d="M92.4 40.6 L96 40" />
  </>
);

const FRAMES = [
  { id: 'wide', label: '1 Wide', art: WIDE },
  { id: 'two-shot', label: '2 Two-shot', art: TWO_SHOT },
  { id: 'close', label: '3 Close', art: CLOSE },
];

// Three storyboard frames of the twilight gate, generated one after another
// and then cleared for the next pass, while on screen. Decorative.
export default function StoryboardRun({ className = '' }) {
  const rootRef = useRef(null);
  const drawn = useScreenLoop(rootRef, LOOP);

  return (
    <div
      ref={rootRef}
      className={`storyboard-run ${drawn === FRAMES.length ? 'is-complete' : ''} ${className}`.trim()}
      aria-hidden="true"
    >
      <div className="storyboard-run-head">
        <span className="storyboard-run-slug">EXT. THE GATE - TWILIGHT</span>
        <span className="storyboard-run-count">
          {drawn}/{FRAMES.length}
        </span>
      </div>
      <div className="storyboard-run-frames">
        {FRAMES.map((frame, index) => (
          <div
            key={frame.id}
            className={`storyboard-run-frame ${index < drawn ? 'is-drawn' : ''}`.trim()}
          >
            <span className="storyboard-run-shot">
              <svg viewBox="0 0 160 90" focusable="false">
                {frame.art}
              </svg>
            </span>
            <span className="storyboard-run-label">{frame.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
